import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Layers, Lock, Plus, Trash2, ArrowUp, ArrowDown, CircleAlert } from 'lucide-react';
import { useAccessToken } from '@/auth/useAccessToken';
import { useMe } from '@/features/me/useMe';
import { useAllApplications } from '@/features/applications/useApplications';
import { applicationsApi, type ApplicationOption, type SaveApplicationInput } from '@/features/applications/applicationsApi';
import { ApiError } from '@/lib/apiClient';
import { cn } from '@/lib/utils';

/**
 * /admin/applications — IT-Admin management of the "Application" dropdown list.
 * Add, rename, reorder, activate/deactivate, and delete options. Non-admins
 * never see the nav link and a direct hit shows a locked card (the API also
 * returns 403 via RequireITAdmin).
 */
export function AdminApplications() {
  const me = useMe();
  const isAdmin = me.data?.role === 'ITAdmin';
  const apps = useAllApplications();
  const forbidden = (apps.error as ApiError)?.status === 403 || (me.data && !isAdmin);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-3xl mx-auto">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <Layers className="w-6 h-6 text-primary" /> Applications
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Manage the options shown in the “Which application?” dropdown on the submit-ticket form.
        </p>
      </header>

      {forbidden && (
        <div className="rounded-xl border border-dashed bg-card p-10 text-center">
          <Lock className="w-6 h-6 mx-auto text-muted-foreground mb-2" />
          <div className="text-base font-medium">Admin access required</div>
          <p className="text-sm text-muted-foreground mt-1.5">Only IT Admins can manage application options.</p>
        </div>
      )}

      {!forbidden && (
        <>
          <AddApplication />
          {apps.isPending && <p className="text-sm text-muted-foreground mt-4">Loading…</p>}
          {apps.isError && !forbidden && (
            <div className="mt-4 rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive flex items-start gap-2">
              <CircleAlert className="w-4 h-4 mt-0.5" />
              <span>Couldn't load applications: {(apps.error as Error).message}</span>
            </div>
          )}
          {apps.data && <ApplicationTable items={apps.data} />}
        </>
      )}
    </div>
  );
}

function AddApplication() {
  const getAccessToken = useAccessToken();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');

  const create = useMutation({
    mutationFn: () => applicationsApi.create(name.trim(), getAccessToken),
    onSuccess: () => {
      setName('');
      void queryClient.invalidateQueries({ queryKey: ['applications'] });
    }
  });

  const duplicate = (create.error as ApiError)?.status === 409;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (name.trim()) create.mutate();
      }}
      className="rounded-xl border bg-card p-4 flex items-start gap-2"
    >
      <div className="flex-1">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={80}
          placeholder="Add an application (e.g. Adobe Acrobat)"
          className="w-full bg-background border rounded-md px-3 py-2 text-sm outline-none focus:ring-2 ring-primary/40"
        />
        {create.isError && (
          <p className="mt-1 text-xs text-destructive">
            {duplicate ? 'An application with that name already exists.' : 'Could not add the application.'}
          </p>
        )}
      </div>
      <button
        type="submit"
        disabled={!name.trim() || create.isPending}
        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 disabled:opacity-60"
      >
        <Plus className="w-4 h-4" /> Add
      </button>
    </form>
  );
}

function ApplicationTable({ items }: { items: ApplicationOption[] }) {
  if (items.length === 0) {
    return (
      <div className="mt-4 rounded-xl border border-dashed bg-card p-10 text-center text-sm text-muted-foreground">
        No applications yet — add one above.
      </div>
    );
  }

  return (
    <div className="mt-4 rounded-xl border bg-card divide-y">
      {items.map((item, i) => (
        <ApplicationRow
          key={item.id}
          item={item}
          isFirst={i === 0}
          isLast={i === items.length - 1}
          prev={items[i - 1]}
          next={items[i + 1]}
        />
      ))}
    </div>
  );
}

function ApplicationRow({
  item,
  isFirst,
  isLast,
  prev,
  next
}: {
  item: ApplicationOption;
  isFirst: boolean;
  isLast: boolean;
  prev?: ApplicationOption;
  next?: ApplicationOption;
}) {
  const getAccessToken = useAccessToken();
  const queryClient = useQueryClient();
  const [name, setName] = useState(item.name);
  const dirty = name.trim() !== item.name;

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['applications'] });

  const save = useMutation({
    mutationFn: (input: SaveApplicationInput) => applicationsApi.update(item.id, input, getAccessToken),
    onSuccess: () => void invalidate()
  });

  const remove = useMutation({
    mutationFn: () => applicationsApi.remove(item.id, getAccessToken),
    onSuccess: () => void invalidate()
  });

  // Reorder by swapping this row's SortOrder with its neighbour's: fire both
  // updates, then invalidate once they've landed so the list re-sorts.
  const reorder = useMutation({
    mutationFn: async (other: ApplicationOption) => {
      await Promise.all([
        applicationsApi.update(item.id, { name: item.name, sortOrder: other.sortOrder, isActive: item.isActive }, getAccessToken),
        applicationsApi.update(other.id, { name: other.name, sortOrder: item.sortOrder, isActive: other.isActive }, getAccessToken)
      ]);
    },
    onSuccess: () => void invalidate()
  });

  const duplicate = (save.error as ApiError)?.status === 409;

  return (
    <div className={cn('flex items-center gap-3 px-4 py-3', !item.isActive && 'opacity-60')}>
      <div className="flex flex-col">
        <button
          type="button"
          disabled={isFirst || reorder.isPending}
          onClick={() => prev && reorder.mutate(prev)}
          className="text-muted-foreground hover:text-foreground disabled:opacity-30"
          aria-label="Move up"
        >
          <ArrowUp className="w-4 h-4" />
        </button>
        <button
          type="button"
          disabled={isLast || reorder.isPending}
          onClick={() => next && reorder.mutate(next)}
          className="text-muted-foreground hover:text-foreground disabled:opacity-30"
          aria-label="Move down"
        >
          <ArrowDown className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={80}
          className="w-full bg-background border rounded-md px-3 py-1.5 text-sm outline-none focus:ring-2 ring-primary/40"
        />
        {save.isError && (
          <p className="mt-1 text-xs text-destructive">
            {duplicate ? 'That name is already in use.' : 'Could not save.'}
          </p>
        )}
      </div>

      {dirty && (
        <button
          type="button"
          disabled={!name.trim() || save.isPending}
          onClick={() => save.mutate({ name: name.trim(), sortOrder: item.sortOrder, isActive: item.isActive })}
          className="px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-xs font-medium hover:opacity-90 disabled:opacity-60"
        >
          Save
        </button>
      )}

      <label className="inline-flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer select-none">
        <input
          type="checkbox"
          checked={item.isActive}
          onChange={(e) => save.mutate({ name: item.name, sortOrder: item.sortOrder, isActive: e.target.checked })}
          className="rounded border-input"
        />
        Active
      </label>

      <button
        type="button"
        disabled={remove.isPending}
        onClick={() => {
          if (confirm(`Delete “${item.name}”? Existing tickets keep their value.`)) remove.mutate();
        }}
        className="text-muted-foreground hover:text-destructive disabled:opacity-40"
        aria-label="Delete"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
}
